import ensureMapsPreconnect from '../../utils/shared/ensureMapsPreconnect';

/** Matches interactive map defaults in useGoogleMaps. */
export const CREATOR_MAP_STATIC_CENTER = {
  lat: 39.8283,
  lng: -98.5795,
};

export const CREATOR_MAP_STATIC_ZOOM = 4;

/** Matches the desktop map pane maxWidth (~560); keep under Static API 640 cap. */
export const CREATOR_MAP_STATIC_WIDTH = 560;
export const CREATOR_MAP_STATIC_HEIGHT = 640;

/**
 * Lightweight Static Maps image for Creator Map LCP.
 * Discovered in public/index.html on `/creator-map` (before bundle.js) via a
 * fixed shell outside #root, then reused by CreatorMapRouteFallback /
 * CreatorMapSimple after the shell is dismissed.
 *
 * Defaults favor bytes over retina sharpness: jpg + scale=1 + map-pane-sized
 * dimensions (placeholder is replaced once interactive tiles load).
 *
 * Keep center/zoom/size/scale/format in sync with the pathname-gated preload
 * script in public/index.html.
 */
export function buildCreatorMapStaticPlaceholderUrl({
  width = CREATOR_MAP_STATIC_WIDTH,
  height = CREATOR_MAP_STATIC_HEIGHT,
  scale = 1,
  mapType = 'roadmap',
  format = 'jpg',
} = {}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;

  const params = new URLSearchParams({
    center: `${CREATOR_MAP_STATIC_CENTER.lat},${CREATOR_MAP_STATIC_CENTER.lng}`,
    zoom: String(CREATOR_MAP_STATIC_ZOOM),
    size: `${Math.min(640, width)}x${Math.min(640, height)}`,
    scale: String(scale),
    maptype: mapType,
    format,
    key: apiKey,
  });

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

/**
 * Warm Maps origins and preload the static placeholder as soon as /creator-map is entered,
 * including during the lazy chunk download (Suspense fallback).
 */
export function ensureCreatorMapStaticPlaceholderPreload(
  url = buildCreatorMapStaticPlaceholderUrl(),
) {
  if (typeof document === 'undefined' || !url) return null;

  ensureMapsPreconnect();

  if (!document.querySelector('link[data-creator-map-static-preload]')) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('data-creator-map-static-preload', 'true');
    document.head.appendChild(link);
  }

  return url;
}
